import React, { useState } from "react";
import { Input, Button, Typography, Space, message, Modal, Table } from "antd";
import { EditOutlined, EyeOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import _commonService from "../../modules/commonService";
import CurrencyInput from "../CurrencyInput";
import "./AdjustmentField.scss";

const { Text } = Typography;

/**
 * Banker/Underwriter Adjustment feature (BankerAdjustmentExecutionPlan.md Section 8.2).
 *
 * Renders the "As Submitted" value the applicant entered (always read-only, never changed here)
 * plus, for Senior Private Banker/Underwriter Banker only, an Adjusted value + Reason the field
 * can be adjusted with. This is the one new abstraction introduced for this feature -- it's
 * instantiated once per adjustable field/line-item across five screens, so building it once here
 * is clearly better than repeating the same read/edit/history markup at every call site.
 *
 * This component has no idea which application, table, or field it represents -- the caller
 * supplies `asSubmittedValue` and the already-resolved `adjustment` (or null), and `onSave` does
 * the actual API call. That keeps this component reusable across both the single-row fields
 * (Income Sources, Expenses, Loan Amount) and the multi-row line items (Balance Sheet), which
 * differ only in what their caller closes over.
 *
 * Props:
 *   label             - field label, e.g. "Salary (Gross)"
 *   asSubmittedValue  - the applicant's submitted value (string/number), or falsy for "N/A"
 *   adjustment        - the current ApplicantBankerAdjustmentModel for this field, or null/undefined if none exists
 *   canEdit           - whether the current user may create/change an adjustment right now
 *                        (role + application status + not-locked, per adjustmentPermissions.js)
 *   onSave(adjustedValue, reason) - async; performs the actual SaveAdjustment call and refreshes
 *                        the caller's overlay state. Rejecting leaves the field in edit mode.
 *   onViewHistory()   - async, returns the full ApplicantBankerAdjustmentModel[] history for this
 *                        field/entry (latest first -- the backend already sorts it that way). Same
 *                        "component doesn't know what it represents" pattern as onSave: the caller
 *                        closes over applicationId/tableName/fieldName/sourceRowId, not this component.
 */
const AdjustmentField = ({ label, asSubmittedValue, adjustment, canEdit, onSave, onViewHistory }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [adjustedValue, setAdjustedValue] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);

  const formatMoney = (value) =>
    value || value === 0 ? `$ ${_commonService.formatNumberWithCommas(value)}` : "N/A";

  const startEditing = () => {
    setAdjustedValue(adjustment?.AdjustedValue ?? "");
    setReason("");
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setAdjustedValue("");
    setReason("");
  };

  const handleSave = async () => {
    // Users often type/paste the adjusted value in the same "$ 454,545" shape the "As Submitted"
    // value is displayed in -- strip the currency formatting before validating/parsing so those
    // values aren't rejected by Number() while a plain unformatted number (e.g. under 1,000, with
    // no comma) passes through fine.
    const sanitizedValue =
      typeof adjustedValue === "string" ? adjustedValue.replace(/[$,\s]/g, "") : adjustedValue;
    if (sanitizedValue === "" || sanitizedValue === null || isNaN(Number(sanitizedValue))) {
      message.error("Enter a valid adjusted value.");
      return;
    }
    if (!reason.trim()) {
      message.error("A reason is required to save an adjustment.");
      return;
    }
    try {
      setSaving(true);
      await onSave(sanitizedValue, reason.trim());
      setIsEditing(false);
      setReason("");
    } catch (err) {
      // onSave already surfaces its own error message (server validation, network failure, etc.)
      // -- keep the form open so the Banker/Underwriter doesn't lose what they typed.
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async () => {
    setIsHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const rows = await onViewHistory();
      setHistoryRows(rows || []);
    } catch (err) {
      // onViewHistory already surfaces its own error message.
      setHistoryRows([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const historyColumns = [
    { title: "Adjusted Value", dataIndex: "AdjustedValue", key: "AdjustedValue", render: formatMoney },
    { title: "Adjusted By", dataIndex: "AdjustedByUserName", key: "AdjustedByUserName" },
    { title: "Role", dataIndex: "AdjustedByRoleName", key: "AdjustedByRoleName" },
    {
      title: "Date",
      dataIndex: "AdjustmentDateTime",
      key: "AdjustmentDateTime",
      render: (value) => (value ? _commonService.formatDateTimeAMPM(value) : "N/A"),
    },
     { title: "Reason", dataIndex: "Reason", key: "Reason" },
  ];

  return (
    <div className="adjustment-field">
      {label && <Text type="secondary">{label}</Text>}
      <div
        className="b-bottm"
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <span>As Submitted: {formatMoney(asSubmittedValue)}</span>
        <Space size={10} style={{ marginLeft: 8 }}>
          {canEdit && !isEditing && (
            <EditOutlined onClick={startEditing} style={{ color: "#595959", cursor: "pointer" }} />
          )}
          {onViewHistory && (
            <EyeOutlined
              onClick={openHistory}
              title="View adjustment history"
              style={{ color: "#595959", cursor: "pointer" }}
            />
          )}
          
        </Space>
      </div>

      {!isEditing && adjustment && (
        <div style={{ marginTop: 4 }}>
          {/*
            A plain styled span instead of antd's <Tag>: this component is mounted in more than
            one place now (FinancialStatementTab.js's own tree, and
            BalanceSheetQuestionnaireDashboard.js, whose card uses the "custom-section" class,
            which carries a `.custom-section .ant-card-body span { color: ... !important; }` rule
            in App.css that forces every plain span's text color -- overriding inline styles, which
            lose to `!important`). The inline style below is a harmless base/fallback; the actual
            override happens in AdjustmentField.scss via a selector specific enough to beat that
            rule unconditionally. See that file for the full explanation.
          */}
          <span
            className={`adjustment-field-badge${adjustment.IsOrphaned ? " adjustment-field-badge--orphaned" : ""}`}
            style={{
              display: "inline-block",
              padding: "1px 8px",
              fontSize: 12,
              lineHeight: "20px",
              borderRadius: 4,
              color: adjustment.IsOrphaned ? "#fa8c16" : "#1890ff",
              background: adjustment.IsOrphaned ? "#fffbe6" : "#e6f4ff",
              border: `1px solid ${adjustment.IsOrphaned ? "#ffd591" : "#91caff"}`,
            }}
          >
            Adjusted: {formatMoney(adjustment.AdjustedValue)}
          </span>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Last adjusted by {adjustment.AdjustedByUserName} ({adjustment.AdjustedByRoleName})
              {adjustment.AdjustmentDateTime
                ? ` on ${_commonService.formatDateTimeAMPM(adjustment.AdjustmentDateTime)}`
                : ""}
              {adjustment.IsLocked ? " -- locked" : ""}
            </Text>
          </div>
          {adjustment.Reason && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Reason: {adjustment.Reason}
              </Text>
            </div>
          )}
          {adjustment.IsOrphaned && (
            <div>
              <Text type="warning" style={{ fontSize: 12 }}>
                This entry has changed since it was adjusted -- please review.
              </Text>
            </div>
          )}
        </div>
      )}

      {isEditing && (
        <div className="adjustment-edit-box">
          <CurrencyInput
            placeholder="Adjusted value"
            value={adjustedValue}
            onChange={(value) => setAdjustedValue(value)}
          />
          <Input.TextArea
            placeholder="Reason (required)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            autoSize={{ minRows: 2, maxRows: 4 }}
          />
          <div className="adjustment-edit-actions">
            <Button
              type="primary"
              size="small"
              icon={<CheckOutlined />}
              loading={saving}
              onClick={handleSave}
            >
              Save
            </Button>
            <Button size="small" icon={<CloseOutlined />} onClick={cancelEditing} disabled={saving}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {onViewHistory && (
        <Modal
          className="adjustment-history-modal"
          title={label ? `Adjustment History -- ${label}` : "Adjustment History"}
          open={isHistoryOpen}
          onCancel={() => setIsHistoryOpen(false)}
          footer={null}
          width={640}
          destroyOnClose
        >
          <Table
            className="transparent-table"
            rowKey="Id"
            size="small"
            loading={historyLoading}
            dataSource={historyRows}
            columns={historyColumns}
            pagination={false}
            scroll={{ y: 300 }}
            locale={{ emptyText: "No adjustments recorded for this field yet." }}
          />
        </Modal>
      )}
    </div>
  );
};

export default AdjustmentField;
